import { Modal, Box, styled } from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import BusinessIcon from '@mui/icons-material/Business';
import { t } from 'i18next';
import { SharedEmailEmploymentView } from '@/models/company';
import {
    PrimaryButton,
    SecondaryButtonRegular
} from '@/components/shared/Button';
import {
    SemiBold,
    SMMedium,
    TextSMSemiBold,
    TextSMRegular
} from '@/typography/Typography';
import { warning100, warning500 } from '@/utils/colors';

type EmailChangeWarningDialogProps = {
    open: boolean;
    employments: SharedEmailEmploymentView[];
    oldEmail: string;
    newEmail: string;
    hasEmploymentsInOtherCompanies: boolean;
    onCancel: () => void;
    onConfirm: () => void;
};

const DialogCard = styled(Box)(({ theme }) => ({
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '400px',
    backgroundColor: theme.palette.background.paper,
    borderRadius: '12px',
    padding: theme.spacing(3),
    boxSizing: 'border-box',
    outline: 'none',
    boxShadow: '0px 8px 24px rgba(16, 24, 40, 0.18)'
}));

const WarningBadge = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '44px',
    height: '44px',
    borderRadius: '50%',
    backgroundColor: warning100,
    color: warning500
});

const EmploymentList = styled(Box)(({ theme }) => ({
    margin: theme.spacing(1.75, 0),
    padding: theme.spacing(0, 1.75),
    border: `1px solid ${theme.palette.greyBorder}`,
    borderRadius: '8px'
}));

const EmploymentRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0),
    '& + &': {
        borderTop: `1px solid ${theme.palette.greyBorder}`
    }
}));

const buildRoleLine = (employment: SharedEmailEmploymentView): string =>
    [
        employment.departmentNames.join(', '),
        employment.employeeGroupNames.join(', ')
    ]
        .filter((part) => part.length > 0)
        .join(' · ');

export const EmailChangeWarningDialog = (
    props: EmailChangeWarningDialogProps
) => {
    const count = props.employments.length;

    return (
        <Modal open={props.open} onClose={props.onCancel}>
            <DialogCard>
                <Box display="flex" justifyContent="center" mb={1.75}>
                    <WarningBadge>
                        <WarningIcon fontSize="medium" />
                    </WarningBadge>
                </Box>

                <TextSMSemiBold textAlign="center">
                    {t('shared_email_confirm_title', { count })}
                </TextSMSemiBold>

                <TextSMRegular
                    color="text.secondary"
                    textAlign="center"
                    mt={1}
                    display="block"
                >
                    {t('shared_email_confirm_body')}
                </TextSMRegular>

                <EmploymentList>
                    {props.employments.map((employment) => (
                        <EmploymentRow key={employment.userId}>
                            <BusinessIcon
                                sx={{ fontSize: 18, color: 'text.secondary' }}
                            />
                            <Box display="flex" flexDirection="column">
                                <SMMedium>{employment.companyName}</SMMedium>
                                {buildRoleLine(employment) && (
                                    <TextSMRegular color="text.secondary">
                                        {buildRoleLine(employment)}
                                    </TextSMRegular>
                                )}
                            </Box>
                        </EmploymentRow>
                    ))}
                </EmploymentList>

                <TextSMRegular
                    color="text.secondary"
                    textAlign="center"
                    display="block"
                >
                    {t('shared_email_from')}{' '}
                    <SemiBold display="inline">{props.oldEmail}</SemiBold>{' '}
                    {t('shared_email_to')}{' '}
                    <SemiBold display="inline">{props.newEmail}</SemiBold>
                </TextSMRegular>

                {props.hasEmploymentsInOtherCompanies && (
                    <TextSMRegular
                        color="text.secondary"
                        textAlign="center"
                        display="block"
                        mt={1}
                    >
                        {t('shared_email_other_companies_note')}
                    </TextSMRegular>
                )}

                <Box display="flex" gap={1.5} mt={2.5}>
                    <SecondaryButtonRegular
                        onClick={props.onCancel}
                        style={{ width: '100%', height: '44px', margin: 0 }}
                    >
                        {t('cancel')}
                    </SecondaryButtonRegular>
                    <PrimaryButton
                        onClick={props.onConfirm}
                        style={{ width: '100%', height: '44px', margin: 0 }}
                    >
                        {t('update_email')}
                    </PrimaryButton>
                </Box>
            </DialogCard>
        </Modal>
    );
};
